import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { ApiError, get, post, type Programme, type RundownDto, type RundownItemDto } from '../api.js';
import { DateInput } from '../date-input.js';
import { PageTitle } from '../frame.js';
import { currentLocale, t } from '../i18n.js';
import { Link } from '../router.js';
import { refusalKind } from '../playout-refusal.js';
import { ClipIcons, clipKinds, clipWords, rowClipList } from '../row-clips.js';
import { useSession } from '../session.js';
import { classes, fmtDateInput, hhmmss, mmss, pad2, rt, todayIso } from '../util.js';
import { realtime, type WsMessage } from '../ws.js';
import { Dialog } from './rundown-dialogs.js';
import type { SyncInfo } from './rundown-grid.js';

interface Player {
  id: string;
  name: string;
  enabled: boolean;
  mode: string | null;
  state: string | null;
}

/**
 * Control room: the gallery board for one bulletin. Large type for the wall screen, the clock, the
 * on-air and next rows, the bulletin against its planned length, and the running order, which the
 * producer changes with big buttons (up, down, play next, to the end, skip) or by dragging. Every
 * change is the same `move` / `float` mutation the editorial grid sends, so the grid, the playout
 * relay and the autocue follow at once; the on-air row cannot be moved. Read-only without the
 * reorder permission. A full-screen mode shows the board alone.
 */

interface TodayRow {
  id: string;
  programmeId: string;
  name: string;
  status: string;
  revision: number;
  count: number;
}

type TextSize = 's' | 'm' | 'l';
const SIZE_KEY = 'airpost.control.size';
const FOLLOW_KEY = 'airpost.control.follow';
const SIZES: TextSize[] = ['s', 'm', 'l'];

/** A calendar day moved by n days, as ISO. */
const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const weekday = (iso: string) => new Intl.DateTimeFormat(currentLocale() === 'el' ? 'el-GR' : 'en-GB', { weekday: 'long' }).format(new Date(`${iso}T00:00:00`));

/**
 * Pick the bulletin for the board: today's bulletins as tiles, and under them any other day, chosen
 * with the date box or the day arrows, listed the same way. Only bulletins that exist are offered;
 * a new one is opened from the rundown guide.
 */
export function ControlRoomSelectPage() {
  const todayDate = todayIso();
  const [today, setToday] = useState<TodayRow[] | null>(null);
  const [date, setDate] = useState(todayDate);
  const [other, setOther] = useState<{ date: string; rundowns: TodayRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    get<{ rundowns: TodayRow[] }>('/api/rundowns/today')
      .then((r) => setToday(r.rundowns))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);
  useEffect(() => {
    if (!date || date === todayDate) return;
    let on = true;
    get<{ date: string; rundowns: TodayRow[] }>(`/api/rundowns/today?date=${date}`)
      .then((r) => {
        if (on) setOther(r);
      })
      .catch((e) => {
        if (on) setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      on = false;
    };
  }, [date, todayDate]);
  const shown = other && other.date === date ? other : null;

  const tiles = (rows: TodayRow[], when: string | null) => (
    <div className="cr-pick">
      {rows.map((r) => (
        <Link key={r.id} className="cr-pick-card" to={`/control/${r.id}`}>
          <span className="cr-pick-name">📺 {r.name}</span>
          <span className="cr-pick-meta">
            {when && <>{when} · </>}
            {r.count} {t('homeRowsUnit')} · {t(r.status as 'draft' | 'linked' | 'published')}
          </span>
        </Link>
      ))}
    </div>
  );
  const openOne = (
    <Link to="/rundown">{t('rundownGuide')} →</Link>
  );

  return (
    <>
      <PageTitle title={t('crTitle')} hint={t('crPickHint')} />
      {error && <div className="banner err">{error}</div>}
      <div className="cr-pick-head">
        {t('todaysRundowns')} · {fmtDateInput(todayDate)}
      </div>
      {today && today.length > 0 && tiles(today, null)}
      {today && today.length === 0 && (
        <p className="muted">
          {t('crNoBulletins')} {openOne}
        </p>
      )}
      <div className="cr-pick-head">{t('crOtherDay')}</div>
      <div className="toolbar cr-pick-date">
        <button type="button" className="btn" onClick={() => setDate(addDays(date || todayDate, -1))} title={t('crPrevDay')} aria-label={t('crPrevDay')}>
          ◀
        </button>
        <DateInput value={date} onChange={setDate} />
        <button type="button" className="btn" onClick={() => setDate(addDays(date || todayDate, 1))} title={t('crNextDay')} aria-label={t('crNextDay')}>
          ▶
        </button>
        <button type="button" className="btn" onClick={() => setDate(addDays(todayDate, -1))}>
          {t('crYesterday')}
        </button>
        <button type="button" className="btn" onClick={() => setDate(addDays(todayDate, 1))}>
          {t('tomorrow')}
        </button>
        {date && <span className="muted">{weekday(date)}</span>}
      </div>
      {!date || date === todayDate ? (
        <p className="muted">{t('crOtherIsToday')}</p>
      ) : shown && shown.rundowns.length > 0 ? (
        tiles(shown.rundowns, fmtDateInput(shown.date))
      ) : shown ? (
        <p className="muted">
          {t('crNoBulletinsOn')} {fmtDateInput(shown.date)}. {openOne}
        </p>
      ) : (
        <p className="muted">{t('loading')}</p>
      )}
    </>
  );
}

const signed = (s: number) => `${s < 0 ? '−' : '+'}${rt(Math.abs(s))}`;
const clock = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

/* Full screen: the Fullscreen API where the browser has it (Safari before 16.4 under the webkit prefix); an iPhone has it
   for video only, so there the board takes the viewport by itself (`.cr.fullscreen`, fixed over the page). */
type PrefixedDocument = Document & { webkitFullscreenEnabled?: boolean; webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => Promise<void> | void };
type PrefixedElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
const fullscreenElement = () => document.fullscreenElement ?? (document as PrefixedDocument).webkitFullscreenElement ?? null;
const fullscreenAvailable = () => document.fullscreenEnabled === true || (document as PrefixedDocument).webkitFullscreenEnabled === true;
const requestFullscreenOf = (el: HTMLElement): (() => Promise<void>) | null => {
  const p = el as PrefixedElement;
  if (typeof el.requestFullscreen === 'function') return () => el.requestFullscreen();
  if (typeof p.webkitRequestFullscreen === 'function') return async () => void (await p.webkitRequestFullscreen!());
  return null;
};
const exitFullscreenOf = (): Promise<void> => {
  const d = document as PrefixedDocument;
  if (typeof document.exitFullscreen === 'function') return document.exitFullscreen();
  return Promise.resolve(d.webkitExitFullscreen?.()).then(() => undefined);
};

export function ControlRoomPage({ id }: { id: string }) {
  const { can } = useSession();
  const canMove = can('rundown.reorder');
  const canLoad = can('rundown.publish');
  /** Whether this user sends the autocue to a story (its button on each story). */
  const canCue = can('autocue.send');
  // loading the bulletin's videos into a playout player: the chooser, and the players seen so far (for their names)
  const [loadDlg, setLoadDlg] = useState<{ playerId: string; voPlayerId: string; busy: boolean; error: string | null } | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [voPlayer, setVoPlayer] = useState<string | null>(null);
  const [rundown, setRundown] = useState<RundownDto | null>(null);
  const [sync, setSync] = useState<SyncInfo>({ links: [], items: [] });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ id: string; over: string | null; after: boolean } | null>(null);
  /** Mutations still travelling to the server. */
  const [pending, setPending] = useState(0);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const revRef = useRef(0);
  const [size, setSize] = useState<TextSize>(() => {
    const s = localStorage.getItem(SIZE_KEY);
    return s === 's' || s === 'l' ? s : 'm';
  });
  const [follow, setFollow] = useState(() => localStorage.getItem(FOLLOW_KEY) !== '0');
  const [now, setNow] = useState(() => new Date());
  const [onAirSince, setOnAirSince] = useState<{ id: string; at: number } | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [live, setLive] = useState(realtime.state);
  const [mediaTitles, setMediaTitles] = useState<Map<string, string>>(new Map());
  const boardRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const onAirRef = useRef<string | null>(null);
  const liveRef = useRef(realtime.state);
  // the autocue: the story at its reading line (as its screens say), and the story it was just sent to
  const [acAt, setAcAt] = useState<string | null>(null);
  const [acSent, setAcSent] = useState<{ id: string; screens: number } | null>(null);
  const acTimer = useRef(0);

  useEffect(() => {
    const h = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(h);
  }, []);
  useEffect(() => {
    const onFs = () => setFullscreen(fullscreenElement() === boardRef.current && boardRef.current !== null);
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);
  // the page behind the board does not scroll while the board stands in for full screen
  useEffect(() => {
    if (!fullscreen) return;
    document.body.classList.add('cr-full');
    return () => document.body.classList.remove('cr-full');
  }, [fullscreen]);
  useEffect(() => localStorage.setItem(SIZE_KEY, size), [size]);
  useEffect(() => localStorage.setItem(FOLLOW_KEY, follow ? '1' : '0'), [follow]);
  // the revision the next mutation is sent against: the latest view, from a response or from the socket
  useEffect(() => {
    revRef.current = rundown?.revision ?? 0;
  }, [rundown]);

  /** Remember when the on-air row changed, for the "on air for" counter. */
  const trackOnAir = useCallback((items: SyncInfo['items']) => {
    const playing = items.find((s) => s.target === 'playout' && s.downstreamStatus === 'playing')?.itemId ?? null;
    if (playing === onAirRef.current) return;
    onAirRef.current = playing;
    setOnAirSince(playing ? { id: playing, at: Date.now() } : null);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await get<{ rundown: RundownDto; sync: SyncInfo }>(`/api/rundowns/${id}`);
      setRundown(r.rundown);
      setSync(r.sync);
      trackOnAir(r.sync.items);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [id, trackOnAir]);

  const onMessage = useCallback(
    (m: WsMessage) => {
      if (m.type === 'rundown' && m.rundownId === id) setRundown(m.view as RundownDto);
      if (m.type === 'sync' && m.rundownId === id) {
        const items = m.items as SyncInfo['items'];
        setSync({ links: m.links as SyncInfo['links'], items });
        trackOnAir(items);
      }
    },
    [id, trackOnAir],
  );
  useEffect(() => {
    const unsub = realtime.subscribe(id);
    const off = realtime.listen(onMessage);
    const initial = window.setTimeout(() => void load(), 0);
    return () => {
      window.clearTimeout(initial);
      unsub();
      off();
    };
  }, [id, load, onMessage]);
  // whether the autocue shows this bulletin (an emptied autocue waits for the next load), looked at again every 15 s
  const [acLoaded, setAcLoaded] = useState<boolean | null>(null);
  const loadAc = useCallback(async () => {
    try {
      setAcLoaded((await get<{ rundownId: string | null }>('/api/autocue/state')).rundownId === id);
    } catch {
      setAcLoaded(null);
    }
  }, [id]);
  useEffect(() => {
    const first = window.setTimeout(() => void loadAc(), 0);
    const every = window.setInterval(() => void loadAc(), 15_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(every);
    };
  }, [loadAc]);
  // where the bulletin's autocue is, for the AC mark on its story
  useEffect(() => {
    const leave = realtime.joinAutocue(id, 'watch');
    const off = realtime.listen((m: WsMessage) => {
      if (m.rundownId === id && (m.type === 'autocue-at' || m.type === 'autocue-go') && typeof m.itemId === 'string') setAcAt(m.itemId);
      if (m.rundownId === id && m.type === 'autocue-unload') {
        setAcAt(null);
        setAcLoaded(false);
      }
    });
    return () => {
      leave();
      off();
      window.clearTimeout(acTimer.current);
    };
  }, [id]);
  /** Every autocue screen of the bulletin to a story; says so when none is open. */
  const cueTo = async (itemId: string) => {
    try {
      const r = await post<{ screens: number }>(`/api/autocue/${id}/go`, { itemId });
      setAcAt(itemId);
      setAcSent({ id: itemId, screens: r.screens });
      if (!r.screens) setNotice(t('crAutocueNone'));
      window.clearTimeout(acTimer.current);
      acTimer.current = window.setTimeout(() => setAcSent(null), 2000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  // the socket's state for the dot; after a reconnect the HTTP load covers a server that restarted meanwhile
  useEffect(
    () =>
      realtime.onState((s) => {
        const was = liveRef.current;
        liveRef.current = s;
        setLive(s);
        if (s === 'live' && was !== 'live') void load();
      }),
    [load],
  );

  const items = useMemo(() => rundown?.items ?? [], [rundown]);
  const mediaIdsKey = [...new Set(items.flatMap((it) => rowClipList(it).map((c) => c.mediaAssetId)))].sort().join(',');
  useEffect(() => {
    if (!mediaIdsKey) return;
    let cancelled = false;
    get<{ media: Array<{ id: string; title: string }> }>(`/api/media?ids=${encodeURIComponent(mediaIdsKey)}&limit=200`)
      .then((r) => !cancelled && setMediaTitles(new Map(r.media.map((m) => [m.id, m.title]))))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [mediaIdsKey]);

  const playoutState = useMemo(() => new Map(sync.items.filter((s) => s.target === 'playout').map((s) => [s.itemId, s])), [sync]);
  const link = sync.links.find((l) => l.target === 'playout');
  const playerName = (ref: string) => players.find((p) => p.id === ref)?.name ?? ref;

  /** Open the chooser with the programme's usual player, the linked one, or the first enabled one. */
  const openLoad = async () => {
    try {
      const [pl, pr] = await Promise.all([get<{ players: Player[]; voPlayer: string | null }>('/api/integrations/players'), get<{ programmes: Programme[] }>('/api/programmes')]);
      setPlayers(pl.players);
      setVoPlayer(pl.voPlayer);
      const usual = pr.programmes.find((p) => p.id === rundown?.programmeId)?.playoutPlayerId;
      const first = pl.players.find((p) => p.enabled) ?? pl.players[0];
      // the VO player: the bulletin's choice, else the installation's (the player named VO), else none
      setLoadDlg({ playerId: link?.targetRef ?? (usual !== null && usual !== undefined ? String(usual) : (first?.id ?? '')), voPlayerId: link?.voPlayer ?? pl.voPlayer ?? 'none', busy: false, error: null });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  const doLoad = async () => {
    if (!loadDlg?.playerId) return;
    setLoadDlg({ ...loadDlg, busy: true, error: null });
    try {
      await post(`/api/rundowns/${id}/link`, { target: 'playout', targetRef: loadDlg.playerId, voPlayer: loadDlg.voPlayerId, replace: true });
      setLoadDlg(null);
      setNotice(t('crLoadSent'));
      await Promise.all([load(), loadAc()]);
    } catch (e) {
      setLoadDlg({ ...loadDlg, busy: false, error: e instanceof ApiError ? e.message : String(e) });
    }
  };
  const onAirIdx = items.findIndex((it) => playoutState.get(it.id)?.downstreamStatus === 'playing');
  const cuedIdx = items.findIndex((it) => playoutState.get(it.id)?.downstreamStatus === 'cued');
  const onAir = onAirIdx >= 0 ? items[onAirIdx]! : null;
  const plays = (it: RundownItemDto) => !it.floated && it.kind !== 'separator';
  const nextIdx = cuedIdx >= 0 ? cuedIdx : onAirIdx >= 0 ? items.findIndex((it, i) => i > onAirIdx && plays(it)) : -1;
  const next = nextIdx >= 0 ? items[nextIdx]! : null;
  const onAirId = onAir?.id ?? null;

  // keep the on-air row in view on the wall screen
  useEffect(() => {
    if (!follow || !onAirId) return;
    document.getElementById(`cr-${onAirId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [onAirId, follow]);

  // timings: done = rows before the on-air one; the on-air row counts from the moment it went on air
  const total = rundown?.totalS ?? 0;
  const planned = rundown?.targetDurationS ?? null;
  const doneS = onAirIdx > 0 ? items.slice(0, onAirIdx).reduce((a, it) => a + (plays(it) ? it.totalS : 0), 0) : 0;
  const elapsedS = onAir && onAirSince?.id === onAir.id ? Math.max(0, Math.round((now.getTime() - onAirSince.at) / 1000)) : 0;
  const remainingS = Math.max(0, total - doneS - elapsedS);
  const endsAt = new Date(now.getTime() + remainingS * 1000);

  // Mutations are applied on screen at once and sent one after the other against the latest revision,
  // so a producer's quick succession of taps is never dropped; a conflict reloads the server's view.
  const send = useCallback(
    (mutations: Array<Record<string, unknown>>): Promise<void> => {
      const run = async () => {
        try {
          const r = await post<{ rundown: RundownDto }>(`/api/rundowns/${id}/mutations`, { baseRevision: revRef.current, mutationId: crypto.randomUUID(), mutations });
          revRef.current = r.rundown.revision;
          setRundown(r.rundown);
          setNotice(null);
        } catch (e) {
          if (e instanceof ApiError && e.status === 409) {
            setNotice(t('conflict'));
            await load();
          } else {
            setError(e instanceof ApiError ? e.message : String(e));
            await load();
          }
        } finally {
          setPending((n) => n - 1);
        }
      };
      setPending((n) => n + 1);
      const p = queueRef.current.then(run, run);
      queueRef.current = p;
      return p;
    },
    [id, load],
  );

  const indexOf = (itemId: string) => items.findIndex((i) => i.id === itemId);
  /** Move a row so that it ends up at `finalIndex` of the list: on screen now, on the server next. */
  const moveTo = (itemId: string, finalIndex: number) => {
    const from = indexOf(itemId);
    const to = Math.max(0, Math.min(items.length - 1, finalIndex));
    if (from < 0 || from === to) return Promise.resolve();
    setRundown((r) => {
      if (!r) return r;
      const next = [...r.items];
      const i = next.findIndex((x) => x.id === itemId);
      if (i < 0) return r;
      const [row] = next.splice(i, 1);
      next.splice(to, 0, row!);
      return { ...r, items: next };
    });
    return send([{ op: 'move', itemId, toIndex: to }]);
  };
  /** Right after the on-air row (or to the top when nothing is on air). */
  const playNext = (itemId: string) => {
    if (onAirIdx < 0) return moveTo(itemId, 0);
    const from = indexOf(itemId);
    return moveTo(itemId, from < onAirIdx ? onAirIdx : onAirIdx + 1);
  };
  const skip = (it: RundownItemDto) => {
    setRundown((r) => (r ? { ...r, items: r.items.map((x) => (x.id === it.id ? { ...x, floated: !it.floated } : x)) } : r));
    return send([{ op: 'float', itemId: it.id, floated: !it.floated }]);
  };
  const movable = (it: RundownItemDto) => canMove && it.id !== onAirId;

  const onDrop = async () => {
    if (!drag || !drag.over || drag.over === drag.id) return setDrag(null);
    const from = indexOf(drag.id);
    const overIdx = indexOf(drag.over);
    let to = overIdx + (drag.after ? 1 : 0);
    if (from < to) to -= 1;
    setDrag(null);
    if (from !== to) await moveTo(drag.id, to);
  };

  const onKey = async (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const idx = cursor ? indexOf(cursor) : -1;
    const it = idx >= 0 ? items[idx]! : null;
    const select = (i: number) => {
      const target = items[Math.max(0, Math.min(items.length - 1, i))];
      if (target) {
        setCursor(target.id);
        document.getElementById(`cr-${target.id}`)?.scrollIntoView({ block: 'nearest' });
      }
    };
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const d = e.key === 'ArrowDown' ? 1 : -1;
      if (e.altKey && it && movable(it)) await moveTo(it.id, idx + d);
      else select(idx + d);
      return;
    }
    if (!it || !movable(it)) return;
    const k = e.key.toLowerCase();
    if (k === 'n' || k === 'ν') await playNext(it.id);
    else if (k === 'e' || k === 'ε') await moveTo(it.id, items.length - 1);
    else if (k === 's' || k === 'σ') await skip(it);
  };

  const toggleFullscreen = async () => {
    const el = boardRef.current;
    if (!el) return;
    if (fullscreenElement()) {
      await exitFullscreenOf().catch(() => setFullscreen(false));
      return;
    }
    if (fullscreen) {
      setFullscreen(false); // the stand-in
      return;
    }
    const request = fullscreenAvailable() ? requestFullscreenOf(el) : null;
    if (request) {
      try {
        await request();
        return;
      } catch {
        // refused (a policy, an embedding): the stand-in below
      }
    }
    setFullscreen(true);
  };

  if (error && !rundown) return <div className="banner err">{error}</div>;
  if (!rundown) return <div>{t('loading')}</div>;

  const rowLabel = (it: RundownItemDto) => `${it.displayNumber ?? ''} ${it.title}`.trim();
  const stateOf = (it: RundownItemDto) => {
    const ps = playoutState.get(it.id);
    if (it.id === onAirId) return t('onAir');
    if (it.id === next?.id) return t('nextUp');
    if (it.floated) return t('crSkipped');
    if (ps?.downstreamStatus) return ps.downstreamStatus;
    if (ps?.state === 'blocked') return t('crRefusedPill');
    if (ps?.state) return ps.state;
    return '';
  };
  /** The playout's refusal of the row's clip, when the relay parked one (ADR 094). */
  const refusalOf = (it: RundownItemDto): string | null => {
    const ps = playoutState.get(it.id);
    return ps?.state === 'blocked' ? ps.blockedReason || t('crRefused') : null;
  };
  const refusalWords = (reason: string) => {
    const kind = refusalKind(reason);
    return kind === 'missing' ? t('crClipMissing') : kind === 'on-air' ? t('crClipOnAir') : `${t('crRefused')}: ${reason}`;
  };

  return (
    <div ref={boardRef} className={classes('cr', `size-${size}`, fullscreen && 'fullscreen')}>
      <div className="cr-head">
        <div className="cr-prog">
          {rundown.programmeName}
          <small>
            {fmtDateInput(rundown.date)} · {t(rundown.status as 'draft' | 'linked' | 'published')} · {items.filter(plays).length} {t('homeRowsUnit')}
          </small>
        </div>
        <div className="cr-clock" aria-live="off">
          {clock(now)}
        </div>
        <div className="cr-tools">
          <span className={classes('cr-dot', live === 'live' ? 'ok' : live === 'connecting' ? 'warn' : 'err')} title={live === 'live' ? t('live') : t('offline')} />
          <button type="button" className={classes('cr-btn', follow && 'on')} onClick={() => setFollow((f) => !f)} title={t('crFollow')}>
            ◎ {t('crFollow')}
          </button>
          <span className="cr-sizes" title={t('crTextSize')}>
            {SIZES.map((s) => (
              <button key={s} type="button" className={classes('cr-btn', size === s && 'on')} onClick={() => setSize(s)}>
                {s === 's' ? 'A' : s === 'm' ? 'A+' : 'A++'}
              </button>
            ))}
          </span>
          {canLoad && (
            <button
              type="button"
              className={classes('cr-btn', 'load', !!link && 'on', (!link || acLoaded === false) && 'flash')}
              onClick={() => void openLoad()}
              title={!link ? `${t('crNotLoaded')} · ${t('crLoadHint')}` : acLoaded === false ? `${t('crAutocueEmpty')} · ${t('crLoadHint')}` : t('crLoadHint')}
            >
              ⏏ {t('crLoad')}
            </button>
          )}
          {link && (
            <span className={classes('cr-link', link.state)} title={link.state === 'blocked' ? t('crLinkRefused').replace('{n}', String(items.filter((it) => refusalOf(it)).length)) : (link.lastError ?? link.state)}>
              {link.state === 'synced' ? '✓' : link.state === 'failed' ? '✖' : link.state === 'blocked' ? '!' : '…'} {playerName(link.targetRef)}
            </span>
          )}
          <button type="button" className="cr-btn" onClick={() => void toggleFullscreen()} title={t('crFullscreen')}>
            {fullscreen ? '⤡' : '⛶'} {t('crFullscreen')}
          </button>
          {!fullscreen && (
            <>
              <Link className="cr-btn" to={`/rundown/${id}`} title={t('gridTitle')}>
                ☰ {t('gridTitle')}
              </Link>
              <Link className="cr-btn" to={`/playout/${id}`} title={t('controlTitle')}>
                🎛 {t('controlShort')}
              </Link>
            </>
          )}
        </div>
      </div>

      {loadDlg && (
        <Dialog title={t('crLoadTitle')} onClose={() => setLoadDlg(null)} width={520}>
          <p>{t('crLoadNote')}</p>
          {players.length === 0 ? (
            <div className="banner warn">{t('crNoPlayers')}</div>
          ) : (
            <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
              <label htmlFor="cr-player">{t('crLoadPlayer')} :</label>
              <select id="cr-player" value={loadDlg.playerId} disabled={loadDlg.busy} onChange={(e) => setLoadDlg({ ...loadDlg, playerId: e.target.value })}>
                {players.map((p) => (
                  <option key={p.id} value={p.id} disabled={!p.enabled}>
                    {p.name}
                    {p.state ? ` · ${p.state}` : ''}
                    {!p.enabled ? ` (${t('inactive')})` : ''}
                  </option>
                ))}
              </select>
              <label htmlFor="cr-vo-player">{t('crLoadVoPlayer')} :</label>
              <select id="cr-vo-player" value={loadDlg.voPlayerId} disabled={loadDlg.busy} onChange={(e) => setLoadDlg({ ...loadDlg, voPlayerId: e.target.value })} title={t('crLoadVoHint')}>
                <option value="none">{t('crLoadVoNone')}</option>
                {players.map((p) => (
                  <option key={p.id} value={p.id} disabled={!p.enabled || p.id === loadDlg.playerId}>
                    {p.name}
                    {p.state ? ` · ${p.state}` : ''}
                    {p.id === voPlayer ? ` (${t('crLoadVoUsual')})` : ''}
                    {!p.enabled ? ` (${t('inactive')})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {loadDlg.voPlayerId !== 'none' && loadDlg.voPlayerId === loadDlg.playerId && <p className="muted small">{t('crLoadVoSame')}</p>}
          {loadDlg.error && <div className="banner err">{loadDlg.error}</div>}
          <div className="toolbar">
            <button className="btn" type="button" onClick={() => setLoadDlg(null)} disabled={loadDlg.busy}>
              {t('cancel')}
            </button>
            <button className="btn primary" type="button" onClick={() => void doLoad()} disabled={loadDlg.busy || !loadDlg.playerId}>
              ⏏ {loadDlg.busy ? t('loading') : t('crLoadGo')}
            </button>
          </div>
        </Dialog>
      )}
      {notice && (
        <div className="cr-banner">
          <span>{notice}</span>
          <button type="button" className="cr-btn" onClick={() => setNotice(null)}>
            OK
          </button>
        </div>
      )}
      {error && <div className="cr-banner err">{error}</div>}

      <div className="cr-tiles">
        <div className="cr-tile air">
          <span className="lab">{t('onAir')}</span>
          <span className="val">{onAir ? rowLabel(onAir) : '—'}</span>
          <span className="sub">{onAir ? `${t('crOnAirFor')} ${mmss(elapsedS)} · ${t('crItemLeft')} ${signed(onAir.totalS - elapsedS)}` : link ? '' : t('crNoPlayout')}</span>
        </div>
        <div className="cr-tile nxt">
          <span className="lab">{t('nextUp')}</span>
          <span className="val">{next ? rowLabel(next) : '—'}</span>
          <span className="sub">{next ? mmss(next.totalS) : ''}</span>
        </div>
        <div className="cr-tile">
          <span className="lab">{t('crTotal')}</span>
          <span className="num">{hhmmss(total)}</span>
          <span className="sub">
            {planned !== null ? (
              <>
                {t('crPlanned')} {hhmmss(planned)} ·{' '}
                <b className={total > planned ? 'cr-over' : 'cr-under'}>
                  {signed(total - planned)} {total > planned ? t('crOver') : t('crUnder')}
                </b>
              </>
            ) : (
              ''
            )}
          </span>
        </div>
        <div className="cr-tile">
          <span className="lab">{t('crRemaining')}</span>
          <span className="num">{hhmmss(remainingS)}</span>
          <span className="sub">
            {t('crEndsAt')} {clock(endsAt)}
          </span>
        </div>
      </div>

      <div ref={listRef} className="cr-list" tabIndex={0} onKeyDown={(e) => void onKey(e)} onDragOver={(e) => e.preventDefault()} onDrop={() => void onDrop()}>
        {items.map((it, idx) => {
          const isAir = it.id === onAirId;
          const isNext = it.id === next?.id;
          const canDrag = movable(it);
          const state = stateOf(it);
          const refusal = refusalOf(it);
          return (
            <div
              key={it.id}
              id={`cr-${it.id}`}
              className={classes('cr-row', cursor === it.id && 'cursor', isAir && 'air', isNext && 'nxt', onAirIdx >= 0 && idx < onAirIdx && 'done', it.floated && 'floated', it.kind === 'separator' && 'separator', drag?.id === it.id && 'dragging', drag?.over === it.id && (drag.after ? 'drop-after' : 'drop-before'))}
              draggable={canDrag}
              onDragStart={(e) => {
                if (!canDrag) return e.preventDefault();
                setDrag({ id: it.id, over: null, after: false });
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const after = e.clientY > rect.top + rect.height / 2;
                setDrag((d) => (d && (d.over !== it.id || d.after !== after) ? { ...d, over: it.id, after } : d));
              }}
              onDragEnd={() => setDrag(null)}
              onClick={() => setCursor(it.id)}
            >
              {canMove && (
                <span className={classes('grip', !canDrag && 'off')} aria-hidden="true">
                  ⠿
                </span>
              )}
              <span className="ord">{it.kind === 'separator' ? '' : it.floated ? '⟂' : it.order}</span>
              <span className="num">{it.kind === 'separator' ? '' : (it.displayNumber ?? '')}</span>
              <span className="title" title={it.title}>
                {it.kind === 'separator' ? (it.title !== '.' ? it.title : '') : it.title}
                {it.kind !== 'story' && it.kind !== 'separator' && <span className="kind">{it.kind}</span>}
              </span>
              <span className={classes('clip', !it.mediaAssetId && it.mediaCode && 'virtual', refusal && 'refused')} title={refusal ? `${refusalWords(refusal)} · ${refusal}` : clipWords(it, mediaTitles) || it.mediaCode || ''}>
                {refusal && (
                  <span className="refused-mark" role="img" aria-label={refusalWords(refusal)}>
                    !
                  </span>
                )}
                {it.mediaAssetId || it.clips.length ? (
                  <ClipIcons {...clipKinds(it)} />
                ) : it.mediaCode ? (
                  `⌛ ${it.mediaCode}`
                ) : it.storyId && it.kind !== 'separator' ? (
                  t('noClip')
                ) : (
                  ''
                )}
              </span>
              <span className="dur">{it.kind === 'separator' ? '' : mmss(it.totalS)}</span>
              <span className="cum">{plays(it) ? rt(it.runningS) : ''}</span>
              <span className="st">{state && <span className="pill">{state}</span>}</span>
              <span className="acts" onClick={(e) => e.stopPropagation()}>
                {canCue && it.kind !== 'separator' && (
                  <button
                    type="button"
                    className={classes('cue', acAt === it.id && 'on', acSent?.id === it.id && (acSent.screens ? 'sent' : 'none'))}
                    title={acAt === it.id ? t('crAutocueHere') : t('crAutocueGo')}
                    aria-label={t('crAutocueGo')}
                    onClick={() => void cueTo(it.id)}
                  >
                    {acSent?.id === it.id ? (acSent.screens ? `AC ✓${acSent.screens > 1 ? acSent.screens : ''}` : 'AC ✕') : 'AC'}
                  </button>
                )}
                {canMove && (
                  <>
                    <button type="button" title={t('moveUp')} disabled={!canDrag || idx === 0} onClick={() => void moveTo(it.id, idx - 1)}>
                      ▲
                    </button>
                    <button type="button" title={t('moveDown')} disabled={!canDrag || idx === items.length - 1} onClick={() => void moveTo(it.id, idx + 1)}>
                      ▼
                    </button>
                    {it.kind !== 'separator' && (
                      <button type="button" className="next" title={t('crPlayNext')} disabled={!canDrag || isAir || (onAirIdx >= 0 && idx === onAirIdx + 1) || (onAirIdx < 0 && idx === 0)} onClick={() => void playNext(it.id)}>
                        ⇥ {t('crPlayNext')}
                      </button>
                    )}
                    <button type="button" title={t('crToEnd')} disabled={!canDrag || idx === items.length - 1} onClick={() => void moveTo(it.id, items.length - 1)}>
                      ⤓
                    </button>
                    {it.kind !== 'separator' && (
                      <button type="button" title={it.floated ? t('crUnskip') : t('crSkip')} disabled={!canDrag} onClick={() => void skip(it)}>
                        {it.floated ? '↩' : '⏭'}
                      </button>
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}
        {items.length === 0 && <p className="muted">—</p>}
      </div>

      <div className="cr-foot">
        <span>
          {pending > 0 && <span className="cr-pending">⏳ </span>}
          {canMove ? t('crKeys') : t('crReadOnly')}
        </span>
        <span>
          {link ? `Playout: ${link.state}${link.lastError ? ` · ${link.lastError}` : ''}` : t('unlinked')}
          {' · '}
          <span className="mouse-only">{t('dragToReorder')}</span>
          <span className="touch-only">{t('holdToReorder')}</span>
        </span>
      </div>
    </div>
  );
}
